import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Building2, Phone, MapPin, Mail, Receipt, FileText, Wrench, FolderKanban, CalendarDays, X, Plus, Upload, FileDown, FileSignature, Trash2 } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { money } from "./billingCommon";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";

const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";

export default function Clients() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDelete = ["owner", "admin"].includes(user?.role);
  const canUpload = ["owner", "admin"].includes(user?.role);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const { data } = useQuery({
    queryKey: ["clients", search],
    queryFn: () => api.get("/clients", { params: search ? { search } : {} }).then(r => r.data),
  });
  const clients = data?.clients || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["clients"] });

  return (
    <div>
      <PageTitle title="Clients" subtitle="Everyone you actively provide services for — separate from leads" action={
        <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setCreating(true)} data-testid="new-client-btn"><Plus className="h-4 w-4" /> New Client</Button>
      } />
      <div className="flex items-center gap-2 mb-4 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input className={`${input} pl-9`} placeholder="Search by client, company, or email…" value={search} onChange={e => setSearch(e.target.value)} data-testid="clients-search" />
        </div>
        <span className="text-sm text-slate-500 whitespace-nowrap">{data?.total ?? 0} clients</span>
      </div>
      <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono">
            <tr><th className="text-left p-3">Client</th><th className="text-left p-3">Company</th><th className="text-left p-3">Activity</th><th className="text-left p-3">Billed</th><th className="text-left p-3">Paid</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {clients.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-slate-400">{search ? "No clients match your search." : "No clients yet. Clients appear here once they have an invoice, estimate, job, project, or appointment."}</td></tr> :
              clients.map((c) => {
                const total = Object.values(c.counts).reduce((a, b) => a + b, 0);
                return (
                  <tr key={c.email} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`client-row-${c.email}`} onClick={() => setActive(c.email)}>
                    <td className="p-3 font-medium text-navy">{c.name}<span className="block text-xs text-slate-400">{c.email}</span></td>
                    <td className="p-3 text-slate-600">{c.company || "—"}</td>
                    <td className="p-3 text-slate-600 text-xs">{total} record{total !== 1 ? "s" : ""}</td>
                    <td className="p-3 text-navy">{money(c.totalBilled)}</td>
                    <td className="p-3 text-navy">{money(c.totalPaid)}</td>
                    <td className="p-3 text-right" onClick={e => e.stopPropagation()}>
                      {canDelete && <ConfirmDelete onConfirm={async () => { await api.delete(`/clients/${encodeURIComponent(c.email)}`); refresh(); toast.success("Client removed from list"); }} title="Remove this client?" description={`${c.name} will be hidden from the Clients list. Their invoices/jobs/etc. are not deleted.`} testid={`client-delete-${c.email}`} />}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {active && <ClientProfile email={active} canDelete={canDelete} canUpload={canUpload} onClose={() => setActive(null)} onChanged={refresh} />}
      {creating && <NewClientDialog onClose={() => setCreating(false)} onSaved={(email) => { setCreating(false); refresh(); setActive(email); }} />}
    </div>
  );
}

function NewClientDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", phone: "", company: "", address: "", notes: "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const email = form.email.trim().toLowerCase();
    if (!email) return toast.error("Email is required");
    try {
      await api.put(`/clients/${encodeURIComponent(email)}`, { name: form.name, phone: form.phone, company: form.company, address: form.address, notes: form.notes });
      toast.success("Client added");
      onSaved(email);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>New Client</DialogTitle><DialogDescription>Add a client manually. Email is the unique identifier.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="new-client-form">
        <input className={input} type="email" placeholder="Email (unique) *" required value={form.email} onChange={e => set("email", e.target.value)} data-testid="new-client-email" />
        <div className="grid grid-cols-2 gap-3">
          <input className={input} placeholder="Name" value={form.name} onChange={e => set("name", e.target.value)} data-testid="new-client-name" />
          <input className={input} placeholder="Phone" value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="new-client-phone" />
        </div>
        <input className={input} placeholder="Company / business name" value={form.company} onChange={e => set("company", e.target.value)} data-testid="new-client-company" />
        <input className={input} placeholder="Address" value={form.address} onChange={e => set("address", e.target.value)} data-testid="new-client-address" />
        <textarea className={`${input} h-auto py-2`} rows={2} placeholder="Notes" value={form.notes} onChange={e => set("notes", e.target.value)} data-testid="new-client-notes" />
        <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="new-client-save">Add Client</Button>
      </form>
    </DialogContent></Dialog>
  );
}

function ClientProfile({ email, canDelete, canUpload, onClose, onChanged }) {
  const { data, refetch } = useQuery({ queryKey: ["client", email], queryFn: () => api.get(`/clients/${encodeURIComponent(email)}`).then(r => r.data), refetchOnMount: "always", staleTime: 0 });
  const [edit, setEdit] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  if (!data) return null;
  const { client, invoices, estimates, jobs, projects, appointments, documents, contracts, totals } = data;

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/clients/${encodeURIComponent(email)}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); refetch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const deleteDoc = async (id) => { await api.delete(`/client-documents/${id}`); refetch(); toast.success("Document removed"); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-profile">
        <DialogHeader><DialogTitle className="flex items-center justify-between pr-6">
          <span>{client.name}</span>
        </DialogTitle><DialogDescription>Client profile, history, documents and contracts.</DialogDescription></DialogHeader>

        {edit ? <ClientEditForm client={client} onSaved={() => { setEdit(false); refetch(); onChanged(); }} onCancel={() => setEdit(false)} /> : (
          <div className="space-y-1 text-sm text-slate-600">
            <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> {client.email}</p>
            {client.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {client.phone}</p>}
            {client.company && <p className="flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /> {client.company}</p>}
            {client.address && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {client.address}</p>}
            {client.notes && <p className="mt-2 text-slate-500 bg-slate-50 rounded-lg p-2 text-xs whitespace-pre-wrap">{client.notes}</p>}
            <div className="flex gap-2 pt-2">
              <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="client-edit-btn">Edit details</Button>
              {canDelete && <ConfirmDelete onConfirm={async () => { await api.delete(`/clients/${encodeURIComponent(client.email)}`); onChanged(); onClose(); toast.success("Client removed from list"); }} title="Remove this client?" description={`${client.name} will be hidden from the Clients list.`} testid="client-profile-delete" label="Remove" />}
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 mt-4">
          <Stat label="Billed" value={money(totals.billed)} />
          <Stat label="Paid" value={money(totals.paid)} />
          <Stat label="Outstanding" value={money(totals.outstanding)} accent />
        </div>

        <div className="mt-4 space-y-3">
          <Section icon={Receipt} title="Invoices" items={invoices} render={(i) => <Row key={i.id} left={i.invoiceNumber} mid={i.status} right={money(i.total)} />} />
          <Section icon={FileText} title="Estimates" items={estimates} render={(e) => <Row key={e.id} left={e.estimateNumber} mid={e.status} right={money(e.total)} />} />
          <Section icon={Wrench} title="Repair Jobs" items={jobs} render={(j) => <Row key={j.id} left={j.jobNumber} mid={j.device} right={j.status} />} />
          <Section icon={FolderKanban} title="Projects" items={projects} render={(p) => <Row key={p.id} left={p.projectNumber} mid={p.projectType} right={p.status} />} />
          <Section icon={CalendarDays} title="Appointments" items={appointments} render={(a) => <Row key={a.id} left={a.serviceType} mid={a.locationType} right={a.status} />} />

          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="flex items-center gap-2 text-xs font-mono uppercase text-slate-400"><FileSignature className="h-3.5 w-3.5" /> Contracts ({(contracts || []).length})</p>
            </div>
            {(contracts || []).length === 0 ? <p className="text-xs text-slate-400 pl-5">None</p> :
              <div className="space-y-1">{contracts.map(c => (
                <div key={c.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-md px-3 py-1.5" data-testid={`client-contract-${c.id}`}>
                  <span className="text-navy">{c.title}</span>
                  <span className="flex items-center gap-2"><span className="text-xs text-slate-500 capitalize">{c.status}</span><span className="text-navy font-medium">{money(c.value)}</span>{c.fileName && <a href={`${API}/contracts/${c.id}/file`} target="_blank" rel="noreferrer" className="text-electric"><FileDown className="h-3.5 w-3.5" /></a>}</span>
                </div>
              ))}</div>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="flex items-center gap-2 text-xs font-mono uppercase text-slate-400"><FileText className="h-3.5 w-3.5" /> Documents ({(documents || []).length})</p>
              {canUpload && <>
                <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx" className="hidden" onChange={uploadDoc} data-testid="client-doc-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1 text-xs text-electric hover:underline" data-testid="client-doc-upload-btn"><Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload"}</button>
              </>}
            </div>
            {(documents || []).length === 0 ? <p className="text-xs text-slate-400 pl-5">No documents. Upload contracts, IDs, or files (PDF, images, DOCX, XLSX — max 25MB).</p> :
              <div className="space-y-1">{documents.map(d => (
                <div key={d.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-md px-3 py-1.5" data-testid={`client-doc-${d.id}`}>
                  <a href={`${API}/client-documents/${d.id}/download`} target="_blank" rel="noreferrer" className="text-navy hover:text-electric flex items-center gap-1.5 truncate"><FileDown className="h-3.5 w-3.5 shrink-0" /> {d.fileName}</a>
                  {canUpload && <button onClick={() => deleteDoc(d.id)} className="text-slate-400 hover:text-red-600 shrink-0" data-testid={`client-doc-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              ))}</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ClientEditForm({ client, onSaved, onCancel }) {
  const [form, setForm] = useState({ name: client.name || "", phone: client.phone || "", company: client.company || "", address: client.address || "", notes: client.notes || "" });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.put(`/clients/${encodeURIComponent(client.email)}`, form); toast.success("Client updated"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="client-edit-form">
      <div className="grid grid-cols-2 gap-3">
        <input className={input} placeholder="Name" value={form.name} onChange={e => set("name", e.target.value)} data-testid="client-name" />
        <input className={input} placeholder="Phone" value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="client-phone" />
      </div>
      <input className={input} placeholder="Company / business name" value={form.company} onChange={e => set("company", e.target.value)} data-testid="client-company" />
      <input className={input} placeholder="Address" value={form.address} onChange={e => set("address", e.target.value)} data-testid="client-address" />
      <textarea className={`${input} h-auto py-2`} rows={3} placeholder="Notes" value={form.notes} onChange={e => set("notes", e.target.value)} data-testid="client-notes" />
      <div className="flex gap-2">
        <Button type="submit" className="bg-electric hover:bg-tech" data-testid="client-save">Save</Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className={`rounded-lg border p-3 text-center ${accent ? "border-electric/30 bg-electric/5" : "border-slate-200 bg-slate-50"}`}>
    <p className="text-[10px] uppercase font-mono text-slate-400">{label}</p>
    <p className={`text-sm font-semibold ${accent ? "text-electric" : "text-navy"}`}>{value}</p>
  </div>
);

const Section = ({ icon: Icon, title, items, render }) => (
  <div>
    <p className="flex items-center gap-2 text-xs font-mono uppercase text-slate-400 mb-1"><Icon className="h-3.5 w-3.5" /> {title} ({items.length})</p>
    {items.length === 0 ? <p className="text-xs text-slate-400 pl-5">None</p> : <div className="space-y-1">{items.map(render)}</div>}
  </div>
);

const Row = ({ left, mid, right }) => (
  <div className="flex items-center justify-between text-sm bg-slate-50 rounded-md px-3 py-1.5">
    <span className="font-mono text-xs text-slate-500">{left}</span>
    <span className="text-slate-500 text-xs capitalize">{mid}</span>
    <span className="text-navy font-medium capitalize">{right}</span>
  </div>
);
